import { type PageBase, useCurrentPage } from '@avensia/excite';
import * as React from 'react';
import { pageToDataLayerInfo } from '../utils';
import { addToDataLayer, type DataLayerEvent } from '..';

const thresholds = [0, 0.25, 0.5, 0.75, 0.9, 1];

let firstRender = true;

type ScrollEventType = {
  pixels_scrolled: number;
  pixels_total: number;
  percent_scrolled: number;
  max_scrolled: number;
  pixels_scrolled_max: number;
};

const defaultEvent: ScrollEventType = Object.freeze({
  percent_scrolled: 0,
  pixels_scrolled: 0,
  pixels_total: 0,
  max_scrolled: 0,
  pixels_scrolled_max: 0,
});

export function useScrollTracking() {
  const scrollEventRef = React.useRef<ScrollEventType>({
    pixels_scrolled: 0,
    pixels_total: 0,
    percent_scrolled: thresholds[0],
    max_scrolled: thresholds[0],
    pixels_scrolled_max: 0,
  });
  const timeoutRef = React.useRef<number | undefined>(undefined);
  const page = useCurrentPage() as PageBase;
  const pageRef = React.useRef(page);

  React.useEffect(() => {
    const triggerEvent = () => sendEvent(true);
    window.addEventListener('beforeunload', triggerEvent);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('beforeunload', triggerEvent);
      window.removeEventListener('scroll', onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendEvent = React.useCallback(
    (unload?: boolean) => {
      if (pageRef.current.url !== page.url) {
        const { url } = pageRef.current;
        const ga4Event: DataLayerEvent = {
          event: 'scroll',
          event_info: {
            percent_scrolled: scrollEventRef.current.percent_scrolled,
            pixels_scrolled: scrollEventRef.current.pixels_scrolled,
            pixels_total: scrollEventRef.current.pixels_total,
            max_scrolled: scrollEventRef.current.max_scrolled,
            pixels_scrolled_max: scrollEventRef.current.pixels_scrolled_max,
            ...pageToDataLayerInfo(pageRef.current),
            page_location: window.location.origin + url,
            page_path: url,
            destination_page: unload ? 'not_available' : page.url,
          },
        };
        addToDataLayer(ga4Event);
        pageRef.current = page;
        scrollEventRef.current = { ...defaultEvent };
      }
    },
    [page],
  );

  React.useEffect(() => {
    if (firstRender) {
      firstRender = false;
      return;
    }
    sendEvent(false);
  }, [page, sendEvent]);

  const onScroll = React.useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(async () => {
      scrollEventRef.current.pixels_scrolled = window.scrollY;
      scrollEventRef.current.pixels_total = document.documentElement.scrollHeight - window.innerHeight;

      if (window.scrollY > scrollEventRef.current.pixels_scrolled_max) {
        scrollEventRef.current.pixels_scrolled_max = window.scrollY;
      }

      const scrollPosition = window.scrollY + window.innerHeight;
      const percentageScrolled = Math.round((scrollPosition / document.documentElement.scrollHeight) * 100) / 100;

      for (let index = 0; index < thresholds.length; index++) {
        if (index + 1 === thresholds.length) {
          if (
            percentageScrolled >= thresholds[index] &&
            scrollEventRef.current.percent_scrolled !== thresholds[index]
          ) {
            scrollEventRef.current.percent_scrolled = thresholds[index];
            if (thresholds[index] > scrollEventRef.current.max_scrolled) {
              scrollEventRef.current.max_scrolled = thresholds[index];
            }
          }
          return;
        }
        if (
          thresholds[index] <= percentageScrolled &&
          percentageScrolled < thresholds[index + 1] &&
          scrollEventRef.current.percent_scrolled !== thresholds[index]
        ) {
          scrollEventRef.current.percent_scrolled = thresholds[index];
          if (thresholds[index] > scrollEventRef.current.max_scrolled) {
            scrollEventRef.current.max_scrolled = thresholds[index];
          }
          return;
        }
      }
    }, 200);
  }, [sendEvent]);
}
